import type { components } from '../../lib/api/openapi-schema';

export type CommunicationSettings = components['schemas']['CommunicationSettings'];
export type BirthdaySettings = components['schemas']['BirthdaySettings'];
export type CommunicationChannel =
  CommunicationSettings['confirmation']['channels'];

export interface CommunicationReminderDraft {
  key: string;
  offsetMinutes: string;
  channels: CommunicationChannel;
}

export interface CommunicationSettingsDraft {
  confirmationChannels: CommunicationChannel;
  cancellationChannels: CommunicationChannel;
  alterationChannels: CommunicationChannel;
  reminders: CommunicationReminderDraft[];
}

export interface BirthdaySettingsDraft {
  channels: CommunicationChannel;
}

export interface CommunicationSettingsDraftErrors {
  reminderOffsets: Record<string, string>;
}

export const COMMUNICATION_CHANNEL_OPTIONS: ReadonlyArray<{
  value: CommunicationChannel;
  label: string;
  hint: string;
}> = [
  {
    value: 'both',
    label: 'Email e SMS',
    hint: 'Envia a comunicação pelos dois canais.',
  },
  {
    value: 'email',
    label: 'Email',
    hint: 'Envia apenas por email.',
  },
  {
    value: 'sms',
    label: 'SMS',
    hint: 'Envia apenas por SMS.',
  },
  {
    value: 'none',
    label: 'Desativado',
    hint: 'Não envia esta comunicação.',
  },
];

const DEFAULT_REMINDER_OFFSET_MINUTES = 1440;
const COMMUNICATION_CHANNEL_VALUES = new Set<CommunicationChannel>([
  'email',
  'sms',
  'both',
  'none',
]);

function createReminderKey() {
  return `reminder-${Math.random().toString(36).slice(2, 10)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isCommunicationChannel(value: unknown): value is CommunicationChannel {
  return typeof value === 'string' && COMMUNICATION_CHANNEL_VALUES.has(value as CommunicationChannel);
}

function cloneCommunicationSettings(
  value: CommunicationSettings
): CommunicationSettings {
  return {
    confirmation: { ...value.confirmation },
    cancellation: { ...value.cancellation },
    alteration: { ...value.alteration },
    reminders: value.reminders.map((reminder) => ({ ...reminder })),
  };
}

function normalizeCommunicationRule(
  value: unknown,
  fallback: CommunicationSettings['confirmation']
) {
  if (!isRecord(value)) {
    return { ...fallback };
  }

  const channels = isCommunicationChannel(value.channels)
    ? value.channels
    : fallback.channels;

  const enabled =
    typeof value.enabled === 'boolean'
      ? value.enabled
      : channels !== 'none';

  return {
    enabled,
    channels,
  };
}

function normalizeReminder(
  value: unknown
): CommunicationSettings['reminders'][number] | null {
  if (!isRecord(value)) {
    return null;
  }

  const offsetMinutes = value.offset_minutes;
  const channels = value.channels;
  const enabled = value.enabled;

  if (
    typeof offsetMinutes !== 'number' ||
    !Number.isInteger(offsetMinutes) ||
    offsetMinutes <= 0 ||
    !isCommunicationChannel(channels)
  ) {
    return null;
  }

  return {
    offset_minutes: offsetMinutes,
    channels,
    enabled: typeof enabled === 'boolean' ? enabled : channels !== 'none',
  };
}

function looksLikeCommunicationSettingsRecord(record: Record<string, unknown>) {
  return (
    'confirmation' in record ||
    'cancellation' in record ||
    'alteration' in record ||
    'reminders' in record
  );
}

function looksLikeBirthdaySettingsRecord(record: Record<string, unknown>) {
  return 'channels' in record || 'enabled' in record;
}

export function createDefaultCommunicationSettings(): CommunicationSettings {
  return {
    confirmation: {
      enabled: true,
      channels: 'both',
    },
    cancellation: {
      enabled: true,
      channels: 'both',
    },
    alteration: {
      enabled: true,
      channels: 'both',
    },
    reminders: [
      {
        offset_minutes: DEFAULT_REMINDER_OFFSET_MINUTES,
        enabled: true,
        channels: 'both',
      },
    ],
  };
}

export function createDefaultBirthdaySettings(): BirthdaySettings {
  return {
    enabled: false,
    channels: 'none',
  };
}

export function normalizeCommunicationSettings(
  value: unknown
): CommunicationSettings | null {
  if (!isRecord(value)) {
    return null;
  }

  const defaults = createDefaultCommunicationSettings();
  const remindersValue = value.reminders;
  const reminders = Array.isArray(remindersValue)
    ? remindersValue
        .map((reminder) => normalizeReminder(reminder))
        .filter(
          (
            reminder
          ): reminder is CommunicationSettings['reminders'][number] =>
            reminder !== null
        )
    : defaults.reminders.map((reminder) => ({ ...reminder }));

  return {
    confirmation: normalizeCommunicationRule(
      value.confirmation,
      defaults.confirmation
    ),
    cancellation: normalizeCommunicationRule(
      value.cancellation,
      defaults.cancellation
    ),
    alteration: normalizeCommunicationRule(
      value.alteration,
      defaults.alteration
    ),
    reminders,
  };
}

export function extractCommunicationSettings(
  value: unknown
): CommunicationSettings | null {
  if (!isRecord(value)) {
    return null;
  }

  if ('communication_settings' in value) {
    return normalizeCommunicationSettings(value.communication_settings);
  }

  if ('communicationSettings' in value) {
    return normalizeCommunicationSettings(value.communicationSettings);
  }

  return looksLikeCommunicationSettingsRecord(value)
    ? normalizeCommunicationSettings(value)
    : null;
}

export function normalizeBirthdaySettings(value: unknown): BirthdaySettings | null {
  if (!isRecord(value)) {
    return null;
  }

  const defaults = createDefaultBirthdaySettings();
  const channels = isCommunicationChannel(value.channels)
    ? value.channels
    : defaults.channels;
  const enabled =
    typeof value.enabled === 'boolean'
      ? value.enabled
      : channels !== 'none';

  return {
    enabled,
    channels,
  };
}

export function extractBirthdaySettings(value: unknown): BirthdaySettings | null {
  if (!isRecord(value)) {
    return null;
  }

  if ('birthday_settings' in value) {
    return normalizeBirthdaySettings(value.birthday_settings);
  }

  if ('birthdaySettings' in value) {
    return normalizeBirthdaySettings(value.birthdaySettings);
  }

  return looksLikeBirthdaySettingsRecord(value)
    ? normalizeBirthdaySettings(value)
    : null;
}

export function createEmptyCommunicationSettingsDraftErrors(): CommunicationSettingsDraftErrors {
  return {
    reminderOffsets: {},
  };
}

export function createCommunicationReminderDraft(): CommunicationReminderDraft {
  return {
    key: createReminderKey(),
    offsetMinutes: String(DEFAULT_REMINDER_OFFSET_MINUTES),
    channels: 'both',
  };
}

export function formatCommunicationReminderOffset(offsetMinutes: string) {
  const trimmedValue = offsetMinutes.trim();

  if (!/^\d+$/.test(trimmedValue)) {
    return null;
  }

  const totalMinutes = Number.parseInt(trimmedValue, 10);

  if (!Number.isInteger(totalMinutes) || totalMinutes <= 0) {
    return null;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(hours === 1 ? '1 hora' : `${hours} horas`);
  }

  if (minutes > 0 || parts.length === 0) {
    parts.push(minutes === 1 ? '1 minuto' : `${minutes} minutos`);
  }

  return `Equivale a ${parts.join(' e ')}.`;
}

export function createCommunicationSettingsDraft(
  value: unknown
): CommunicationSettingsDraft {
  const settings =
    extractCommunicationSettings(value) ?? createDefaultCommunicationSettings();

  return {
    confirmationChannels: settings.confirmation.channels,
    cancellationChannels: settings.cancellation.channels,
    alterationChannels: settings.alteration.channels,
    reminders: settings.reminders.map((reminder) => ({
      key: createReminderKey(),
      offsetMinutes: String(reminder.offset_minutes),
      channels: reminder.channels,
    })),
  };
}

export function createBirthdaySettingsDraft(value: unknown): BirthdaySettingsDraft {
  const settings =
    extractBirthdaySettings(value) ?? createDefaultBirthdaySettings();

  return {
    channels: settings.channels,
  };
}

export function validateCommunicationSettingsDraft(
  draft: CommunicationSettingsDraft
): CommunicationSettingsDraftErrors {
  const nextErrors = createEmptyCommunicationSettingsDraftErrors();

  draft.reminders.forEach((reminder) => {
    const parsedOffset = Number.parseInt(reminder.offsetMinutes.trim(), 10);

    if (
      !reminder.offsetMinutes.trim() ||
      !Number.isInteger(parsedOffset) ||
      parsedOffset <= 0
    ) {
      nextErrors.reminderOffsets[reminder.key] =
        'Introduza um número inteiro de minutos superior a zero.';
    }
  });

  return nextErrors;
}

export function hasCommunicationSettingsDraftErrors(
  errors: CommunicationSettingsDraftErrors
) {
  return Object.keys(errors.reminderOffsets).length > 0;
}

export function buildCommunicationSettingsPayload(
  draft: CommunicationSettingsDraft
): CommunicationSettings {
  return {
    confirmation: {
      enabled: draft.confirmationChannels !== 'none',
      channels: draft.confirmationChannels,
    },
    cancellation: {
      enabled: draft.cancellationChannels !== 'none',
      channels: draft.cancellationChannels,
    },
    alteration: {
      enabled: draft.alterationChannels !== 'none',
      channels: draft.alterationChannels,
    },
    reminders: draft.reminders.map((reminder) => ({
      offset_minutes: Number.parseInt(reminder.offsetMinutes.trim(), 10),
      enabled: reminder.channels !== 'none',
      channels: reminder.channels,
    })),
  };
}

export function buildBirthdaySettingsPayload(
  draft: BirthdaySettingsDraft
): BirthdaySettings {
  return {
    enabled: draft.channels !== 'none',
    channels: draft.channels,
  };
}

export function getCommunicationSettingsOrDefault(value: unknown) {
  return (
    extractCommunicationSettings(value) ?? cloneCommunicationSettings(createDefaultCommunicationSettings())
  );
}

export function getBirthdaySettingsOrDefault(value: unknown) {
  return extractBirthdaySettings(value) ?? createDefaultBirthdaySettings();
}
